import React from 'react';
import clsx from 'clsx';
import { IconStatus } from './documents-to-send/IconStatus';
import { Button, GenericBadge, Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';
import { TooltipComponent } from '@/components/common/tooltip';
import { AddInstructionsDoc } from './';
import { DocumentRequiredPayloadItem } from '@/interfaces';

interface Props {
	type?: 'document-requested' | 'document-to-send';
	title: string;
	badges: string[];
	/** Badges that represent a signature field, regardless of their label. */
	signatureBadges?: string[];
	prompts?: string[];
	isCompleted?: boolean;
	onClick?: () => void;
	onDelete: () => void;
	onEdit?: () => void;
	onDoubleClick?: () => void;
	className?: string;
	customIcon?: string;
	documentInfo?: DocumentRequiredPayloadItem;
}

export const DocumentCard: React.FC<Props> = ({
	type = 'document-to-send',
	title,
	badges,
	signatureBadges = [],
	prompts = [],
	isCompleted,
	onClick,
	onDelete,
	onEdit,
	onDoubleClick,
	className,
	customIcon,
	documentInfo,
}) => {
	const showIcon = !title.toLowerCase().includes('retainer');
	return (
		<div
			onDoubleClick={onDoubleClick}
			className={clsx(
				'border border-[#0D061614] rounded-2xl p-4 flex justify-between items-center group mt-3 relative',
				{
					'hover:border-primary': isCompleted,
					'cursor-pointer select-none': !!onDoubleClick,
				},
				className
			)}
		>
			<div className="flex flex-col lg:flex-row gap-[18px] flex-1 md:min-w-0">
				<IconStatus
					isCompleted={isCompleted ?? false}
					type={type}
					customIcon={customIcon}
				/>
				<div className="flex flex-col gap-2 flex-1 min-w-0">
					<div
						className="flex flex-wrap items-center justify-between gap-1"
						onDoubleClick={(e) => e.stopPropagation()}
					>
						<Typography type="semibold-16">{title}</Typography>
						{type === 'document-requested' && documentInfo && (
							<AddInstructionsDoc documentsData={documentInfo} />
						)}
					</div>
					<div className="flex items-center gap-1 flex-wrap">
						{badges.map((badge, index) => (
							<GenericBadge
								showTooltip
								key={index}
								label={badge}
								colorStatus="gray"
								typographyType="regular-12"
								classNameContainer="rounded-[50px] py-[2px] !px-2 max-w-[151px]"
								icon={
									showIcon &&
									(signatureBadges.includes(badge) ||
										badge.toLowerCase().includes('signature'))
										? Icons.path
										: undefined
								}
							/>
						))}
					</div>
					{prompts.length > 0 && (
						<div className="max-h-[100px] overflow-y-auto rounded-lg p-2 bg-[#FBFBFB] mr-2 scroll-custom">
							<div className="flex items-center gap-1 shrink-0">
								<Icon
									src={Icons.wand}
									width={16}
									height={16}
									className="text-primary"
								/>
								<Typography type="semibold-12">AI Prompt</Typography>
							</div>
							<div className="flex flex-col gap-2 min-w-0 flex-1 mt-3">
								{prompts.map((prompt, index) => (
									<div
										key={index}
										className="border border-[#0D061633] bg-grey-100 rounded-[32px] py-2 px-6 self-start max-w-full"
									>
										<Typography
											type="regular-12"
											className="line-clamp-2 break-words"
										>
											{prompt}
										</Typography>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</div>
			<div className="">
				{title === 'Retainer' && onClick && !isCompleted && (
					<Button
						type="button"
						decoration="primary"
						size="medium"
						className="!h-[30px]"
						onClick={onClick}
					>
						<Typography type="semibold-16" className="text-white">
							Configure
						</Typography>
					</Button>
				)}
				{isCompleted && (
					<div className="flex items-center gap-4 ml-1">
						<Icon
							src={Icons.check}
							width={20}
							height={20}
							className={clsx('transition-all ease-out', {
								'group-hover:hidden': showIcon,
							})}
						/>
						{onEdit && (
							<TooltipComponent
								label="Edit"
								trigger={
									<button
										className={clsx('hidden transition-all ease-in', {
											'group-hover:block': showIcon,
										})}
										onClick={onEdit}
									>
										<Icon src={Icons.editTwo} width={20} height={20} />
									</button>
								}
							/>
						)}
						<button
							className={clsx('hidden transition-all ease-in', {
								'group-hover:block': showIcon,
							})}
							onClick={onDelete}
						>
							<Icon src={Icons.trash} width={20} height={20} />
						</button>
					</div>
				)}
			</div>
		</div>
	);
};
